import { useSnackbar } from 'notistack';
import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  clearTrialUpgradedCheckoutQuery,
  isTrialUpgradedCheckout,
  TRIAL_UPGRADED_NOTICE_MESSAGE,
} from '../subscriptions/checkoutTrialUpgraded';

/**
 * After skip_trial ends an in-progress Stripe trial, the API redirects with
 * ``?checkout=trial_upgraded``. Show a notice and optionally refresh data.
 */
export function useCheckoutTrialUpgradedNotice(onTrialUpgraded?: () => void | Promise<void>) {
  const [searchParams, setSearchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const handled = useRef(false);
  const onTrialUpgradedRef = useRef(onTrialUpgraded);
  onTrialUpgradedRef.current = onTrialUpgraded;

  useEffect(() => {
    if (handled.current || !isTrialUpgradedCheckout(searchParams)) {
      return;
    }
    handled.current = true;
    setSearchParams(clearTrialUpgradedCheckoutQuery(searchParams), { replace: true });

    void (async () => {
      try {
        await onTrialUpgradedRef.current?.();
      } finally {
        enqueueSnackbar(TRIAL_UPGRADED_NOTICE_MESSAGE, {
          variant: 'info',
          autoHideDuration: 12_000,
        });
      }
    })();
  }, [enqueueSnackbar, searchParams, setSearchParams]);
}
